const BUDAPEST_TIME_ZONE = 'Europe/Budapest';

const BUDAPEST_LATITUDE = 47.4979;
const BUDAPEST_LONGITUDE = 19.0402;
const SUNRISE_SUNSET_ZENITH = 90.833;

const budapestDateKeyFormatter = new Intl.DateTimeFormat('hu-HU', {
  timeZone: BUDAPEST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const budapestOffsetFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUDAPEST_TIME_ZONE,
  timeZoneName: 'shortOffset',
});

const padDatePart = (value) => String(value).padStart(2, '0');

const parseDateKey = (dateKey) => {
  if (typeof dateKey !== 'string') {
    return null;
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return { year, month, day };
};

const getDateKeyFromParts = ({ year, month, day }) =>
  `${year}-${padDatePart(month)}-${padDatePart(day)}`;

const getBudapestDateParts = (date = new Date()) => {
  const parts = budapestDateKeyFormatter.formatToParts(date);
  const values = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') {
      values[part.type] = Number(part.value);
    }
  });

  return {
    year: values.year,
    month: values.month,
    day: values.day,
  };
};

const addDaysToDateParts = (parts, dayCount) => {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
  date.setUTCDate(date.getUTCDate() + dayCount);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const getDayOfYear = ({ year, month, day }) => {
  const start = Date.UTC(year, 0, 0);
  const current = Date.UTC(year, month - 1, day);
  return Math.floor((current - start) / 86400000);
};

const normalizeDegrees = (value) => ((value % 360) + 360) % 360;

const normalizeHours = (value) => ((value % 24) + 24) % 24;

const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

const radiansToDegrees = (radians) => (radians * 180) / Math.PI;

const getSunTimeUtcHours = (dateParts, isSunrise) => {
  const dayOfYear = getDayOfYear(dateParts);
  const longitudeHour = BUDAPEST_LONGITUDE / 15;
  const approximateTime = dayOfYear + ((isSunrise ? 6 : 18) - longitudeHour) / 24;
  const meanAnomaly = (0.9856 * approximateTime) - 3.289;
  const trueLongitude = normalizeDegrees(
    meanAnomaly +
      (1.916 * Math.sin(degreesToRadians(meanAnomaly))) +
      (0.020 * Math.sin(degreesToRadians(2 * meanAnomaly))) +
      282.634,
  );

  let rightAscension = normalizeDegrees(
    radiansToDegrees(Math.atan(0.91764 * Math.tan(degreesToRadians(trueLongitude)))),
  );
  const longitudeQuadrant = Math.floor(trueLongitude / 90) * 90;
  const rightAscensionQuadrant = Math.floor(rightAscension / 90) * 90;
  rightAscension = (rightAscension + longitudeQuadrant - rightAscensionQuadrant) / 15;

  const sinDeclination = 0.39782 * Math.sin(degreesToRadians(trueLongitude));
  const cosDeclination = Math.cos(Math.asin(sinDeclination));
  const cosHourAngle = (
    Math.cos(degreesToRadians(SUNRISE_SUNSET_ZENITH)) -
    (sinDeclination * Math.sin(degreesToRadians(BUDAPEST_LATITUDE)))
  ) / (cosDeclination * Math.cos(degreesToRadians(BUDAPEST_LATITUDE)));

  if (cosHourAngle > 1 || cosHourAngle < -1) {
    return null;
  }

  const hourAngle = (isSunrise
    ? 360 - radiansToDegrees(Math.acos(cosHourAngle))
    : radiansToDegrees(Math.acos(cosHourAngle))) / 15;
  const localMeanTime = hourAngle + rightAscension - (0.06571 * approximateTime) - 6.622;

  return normalizeHours(localMeanTime - longitudeHour);
};

const getBudapestOffsetMinutes = (date) => {
  const timeZoneName = budapestOffsetFormatter
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;
  const match = timeZoneName?.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) {
    return 0;
  }

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3] || 0);
  return sign * (hours * 60 + minutes);
};

const formatBudapestDateKey = (date = new Date()) =>
  getDateKeyFromParts(getBudapestDateParts(date));

export const getBudapestTodayKey = () => formatBudapestDateKey(new Date());

const getBudapestDateForDisplay = (dateKey) => {
  const parts = parseDateKey(dateKey);
  if (!parts) {
    return null;
  }

  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
};

export const getBudapestDateAtTime = (dateKey, hour = 0, minute = 0) => {
  const parts = parseDateKey(dateKey);
  if (!parts) {
    return null;
  }

  const utcGuess = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute));
  const offsetMinutes = getBudapestOffsetMinutes(utcGuess);
  return new Date(utcGuess.getTime() - offsetMinutes * 60 * 1000);
};

export const getBudapestTrainingDeadlineDate = (trainingDateKey, hour) => {
  const parts = parseDateKey(trainingDateKey);
  if (!parts) {
    return null;
  }

  const previousDay = addDaysToDateParts(parts, -1);
  return getBudapestDateAtTime(getDateKeyFromParts(previousDay), hour);
};

export const formatDateKeyInBudapest = (dateKey, options) => {
  const date = getBudapestDateForDisplay(dateKey);
  if (!date) {
    return dateKey;
  }

  return date.toLocaleDateString('hu-HU', {
    timeZone: BUDAPEST_TIME_ZONE,
    ...options,
  });
};

export const formatDateTimeInBudapest = (date, options) =>
  date.toLocaleString('hu-HU', {
    timeZone: BUDAPEST_TIME_ZONE,
    ...options,
  });

export const getBudapestSunTimes = (date = new Date()) => {
  const dateParts = getBudapestDateParts(date);
  const sunriseUtcHours = getSunTimeUtcHours(dateParts, true);
  const sunsetUtcHours = getSunTimeUtcHours(dateParts, false);
  const dayStartUtc = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);

  return {
    sunrise: sunriseUtcHours === null ? null : new Date(dayStartUtc + sunriseUtcHours * 60 * 60 * 1000),
    sunset: sunsetUtcHours === null ? null : new Date(dayStartUtc + sunsetUtcHours * 60 * 60 * 1000),
  };
};

export const getUpcomingBudapestSundays = (count = 5) => {
  const todayParts = getBudapestDateParts(new Date());
  const todayWeekday = new Date(Date.UTC(todayParts.year, todayParts.month - 1, todayParts.day, 12)).getUTCDay();
  const daysUntilSunday = todayWeekday === 0 ? 0 : 7 - todayWeekday;
  const firstSunday = addDaysToDateParts(todayParts, daysUntilSunday);

  return Array.from({ length: count }, (_, index) => {
    const sundayParts = addDaysToDateParts(firstSunday, index * 7);
    const key = getDateKeyFromParts(sundayParts);
    return {
      key,
      label: formatDateKeyInBudapest(key, {
        month: 'short',
        day: 'numeric',
        weekday: 'long',
      }),
    };
  });
};
